import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { copyToClipboard } from '../../../utils/copy-to-clipboard';
import { getOpacity } from '../../../theme/utils/get-rgb';
import { CampaignPromoBannerBadge } from './badge/campaign-promo-banner-badge';
import { CampaignPromoBannerCode } from './code/campaign-promo-banner-code';
import { CampaignPromoBannerContent } from './content/campaign-promo-banner-content';

const PROMO_CODE = 'PREDEPIS2';

export const CampaignPromoBanner: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        if (!isCopied) {
            return undefined;
        }

        const timeout = window.setTimeout(() => setIsCopied(false), 3000);

        return () => window.clearTimeout(timeout);
    }, [isCopied]);

    const handleCopyCode = async () => {
        const copied = await copyToClipboard(PROMO_CODE);

        if (copied) {
            setIsCopied(true);
        }
    };

    return (
        <div className="relative mb-medium mt-[1.6rem] lg:mt-[1.8rem]">
            <CampaignPromoBannerBadge
                style={{
                    background: theme.palette.gradient.primary.main,
                    color: theme.palette.gradient.primary.text,
                    boxShadow: `0 0.8rem 1.6rem ${getOpacity(theme.palette.color.primary.main, 0.12)}`,
                }}
            >
                {t('layout.campaignPromoBanner.badge')}
            </CampaignPromoBannerBadge>
            <div
                className="relative overflow-hidden rounded-xlarge border border-solid px-medium pb-medium pt-[3.2rem] text-brand-secondary lg:px-[2.8rem] lg:pb-[2rem] lg:pr-[2.4rem]"
                style={{
                    borderColor: getOpacity(theme.palette.color.primary.main, 0.14),
                    backgroundColor: theme.palette.color.white.main,
                    backgroundImage: `linear-gradient(135deg, ${getOpacity(
                        theme.palette.color.primary.main,
                        0.12,
                    )} 0%, ${getOpacity(theme.palette.color.blue.main, 0.06)} 45%, ${getOpacity(
                        theme.palette.color.secondary.main,
                        0.05,
                    )} 100%)`,
                    boxShadow: `0 1.2rem 3.2rem ${getOpacity(theme.palette.color.primary.main, 0.12)}`,
                    color: theme.palette.color.secondary.main,
                }}
            >
                <div
                    className="absolute inset-y-0 left-0 w-[0.4rem]"
                    style={{ background: theme.palette.gradient.primary.main }}
                />
                <div
                    className="pointer-events-none absolute right-[-6rem] top-[-8rem] h-[14rem] w-[14rem] rounded-full"
                    style={{ background: getOpacity(theme.palette.color.primary.main, 0.08) }}
                />
                <div className="relative z-[1] flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-[2.4rem]">
                    <CampaignPromoBannerContent
                        title={t('layout.campaignPromoBanner.title')}
                        description={t('layout.campaignPromoBanner.description')}
                    />
                    <CampaignPromoBannerCode
                        code={PROMO_CODE}
                        copied={isCopied}
                        copiedLabel={t('layout.campaignPromoBanner.copiedLabel')}
                        copyLabel={t('layout.campaignPromoBanner.copyLabel')}
                        onCopyCode={handleCopyCode}
                        wrapperStyle={{
                            borderColor: getOpacity(theme.palette.color.primary.main, 0.16),
                            boxShadow: `0 0.8rem 1.6rem ${getOpacity(theme.palette.color.primary.main, 0.12)}`,
                        }}
                        buttonStyle={{
                            background: theme.palette.gradient.primary.main,
                            color: theme.palette.gradient.primary.text,
                            transitionDuration: theme.base.transition,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
